import { useCallback, useState } from "react";
import {
  createJoinRequestApi,
  fetchPublicOrgBranchesApi,
} from "../../api/verification";
import {
  branchLabel,
  useOrganisationBranches,
} from "../../hooks/useOrganisationBranches";
import { useIsMobile } from "../../hooks/useIsMobile";
import { C, font } from "../../styles/tokens";
import { InlineIcon } from "../InlineIcon";

export function OrganisationAlreadyRegisteredCard({
  data,
  defaultEmail = "",
  onReset,
}) {
  const isMobile = useIsMobile();
  const [showForm, setShowForm] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState(defaultEmail || "");
  const [phone, setPhone] = useState("");
  const [designation, setDesignation] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const orgName = data?.organisationName || "this Organisation";
  // Only organisations take join requests; an institution's admin adds
  // officers by invitation. No id means no branch lookup for institutions.
  const isInstitution = data?.registeredAs === "ACADEMIC_INSTITUTION";
  const orgId = isInstitution ? null : data?.organisationId;

  // Branches of THIS organisation only. If the applicant re-runs verification
  // against a different company, the hook clears the previously picked branch
  // before the new organisation's list loads.
  const fetchBranches = useCallback((id) => fetchPublicOrgBranchesApi(id), []);
  const {
    branches,
    loading: branchesLoading,
    error: branchesError,
  } = useOrganisationBranches(orgId, fetchBranches, branchId, setBranchId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim() || submitting || !orgId) return;

    setError(null);
    setSubmitting(true);

    try {
      await createJoinRequestApi(orgId, {
        fullName: fullName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim() || undefined,
        designation: designation.trim() || undefined,
        employeeId: employeeId.trim() || undefined,
        // Only ever a branch of orgId -- the server re-validates that.
        branchId: branchId || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setError(
        err?.message ||
          "Failed to submit join request. Please try again or contact your administrator.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (isInstitution) {
    return (
      <div style={containerStyle}>
        <div style={institutionTitleStyle}>Institution Already Registered</div>
        <div style={institutionTextStyle}>
          <strong>{orgName}</strong> is already registered on TickAndPass. Ask
          its administrator to invite you as an officer; the invitation arrives
          by email.
        </div>
        {onReset && (
          <button type="button" onClick={onReset} style={resetBtnStyle}>
            Enter Different Details
          </button>
        )}
        <div style={footerStyle}>
          Need help?{" "}
          <a href="mailto:support@tickandpass.com" style={supportLinkStyle}>
            Contact TickAndPass Support
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>
              Organisation Already Registered
            </div>
            <div style={{ fontSize: 13, color: "#475569", marginTop: 2 }}>
              <strong style={{ color: "#1e293b" }}>{orgName}</strong> is already
              registered on TickAndPass under this identifier.
            </div>
          </div>
        </div>
      </div>

      {!submitted ? (
        <div>
          {!showForm ? (
            <div
              style={{
                marginTop: 14,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => setShowForm(true)}
                style={{
                  ...primaryBtnStyle,
                  width: isMobile ? "100%" : "auto",
                  minHeight: isMobile ? 44 : 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Request to Join {orgName}
              </button>
              {onReset && (
                <button
                  type="button"
                  onClick={onReset}
                  style={{
                    ...secondaryBtnStyle,
                    width: isMobile ? "100%" : "auto",
                    minHeight: isMobile ? 44 : 36,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Enter Different Details
                </button>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ marginTop: 14 }}>
              <div style={noticeBoxStyle}>
                Fill in your details below. An administrator at {orgName} will
                receive your request to approve your officer account.
              </div>

              {error && <div style={errorStyle}>{error}</div>}

              <div
                style={{
                  ...gridStyle,
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                }}
              >
                <div>
                  <label style={labelStyle}>Full Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Rahul Sharma"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Work Email *</label>
                  <input
                    type="email"
                    required
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Mobile Phone</label>
                  <input
                    type="tel"
                    placeholder="e.g. 9876543210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Designation / Role</label>
                  <input
                    type="text"
                    placeholder="e.g. HR Specialist / Branch Manager"
                    value={designation}
                    onChange={(e) => setDesignation(e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Employee ID (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. EMP-10492"
                    value={employeeId}
                    onChange={(e) => setEmployeeId(e.target.value)}
                    style={inputStyle}
                  />
                </div>

                {/* Branch of THIS organisation. Never shows another
                    organisation's branches, and never offers an inactive one. */}
                <div>
                  <label style={labelStyle}>Branch / Location</label>
                  {branchesLoading ? (
                    <div style={branchNoticeStyle}>Loading branches...</div>
                  ) : branchesError ? (
                    <div style={branchNoticeStyle}>
                      Branches unavailable right now. Your administrator can set
                      this after approval.
                    </div>
                  ) : branches.length === 0 ? (
                    <div style={branchNoticeStyle}>
                      No branches available. Your administrator can assign one
                      after approval.
                    </div>
                  ) : (
                    <select
                      value={branchId}
                      onChange={(e) => setBranchId(e.target.value)}
                      style={inputStyle}
                    >
                      <option value="">Select your branch (optional)</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {branchLabel(b)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              <div
                style={{
                  marginTop: 16,
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: 10,
                }}
              >
                <button
                  type="submit"
                  disabled={submitting || !fullName.trim() || !email.trim()}
                  style={{
                    ...primaryBtnStyle,
                    width: isMobile ? "100%" : "auto",
                    minHeight: isMobile ? 44 : 36,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity:
                      submitting || !fullName.trim() || !email.trim() ? 0.6 : 1,
                    cursor:
                      submitting || !fullName.trim() || !email.trim()
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {submitting ? "Submitting Request..." : "Submit Join Request"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  disabled={submitting}
                  style={{
                    ...secondaryBtnStyle,
                    width: isMobile ? "100%" : "auto",
                    minHeight: isMobile ? 44 : 36,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div style={footerStyle}>
            Not affiliated with this organisation?{" "}
            <a
              href="mailto:support@tickandpass.com"
              style={{ color: "#0284c7", textDecoration: "underline" }}
            >
              Contact TickAndPass Support
            </a>
          </div>
        </div>
      ) : (
        <div style={successBoxStyle}>
          <div style={{ fontSize: 24, marginBottom: 6, color: "#047857" }}>
            <InlineIcon name="check" size={24} />
          </div>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#065f46" }}>
            Join Request Submitted
          </div>
          <div
            style={{
              fontSize: 13,
              color: "#047857",
              marginTop: 4,
              lineHeight: "1.45",
            }}
          >
            Your request to join <strong>{orgName}</strong> has been received.
            The organisation administrator has been notified and you will
            receive an email with account activation instructions once approved.
          </div>
          {onReset && (
            <button
              type="button"
              onClick={onReset}
              style={{
                ...secondaryBtnStyle,
                marginTop: 12,
                width: isMobile ? "100%" : "auto",
                minHeight: isMobile ? 44 : 36,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Back to Registration
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const containerStyle = {
  marginTop: 12,
  padding: 16,
  background: "#f0fdf4",
  border: "1px solid #86efac",
  borderRadius: 8,
  fontFamily: font,
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const primaryBtnStyle = {
  background: "#0284c7",
  color: "#ffffff",
  border: "none",
  borderRadius: 6,
  padding: "9px 16px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

const secondaryBtnStyle = {
  background: "#ffffff",
  color: "#475569",
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  padding: "9px 14px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const noticeBoxStyle = {
  padding: "10px 12px",
  background: "#ffffff",
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  fontSize: 12,
  color: "#334155",
  marginBottom: 12,
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
};

const labelStyle = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "#334155",
  marginBottom: 4,
};

const inputStyle = {
  width: "100%",
  padding: "9px 10px",
  fontSize: 14,
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  boxSizing: "border-box",
  outline: "none",
};

/** Shown in place of the branch select when there is nothing to choose from. */
const branchNoticeStyle = {
  padding: "8px 10px",
  fontSize: 12,
  color: "#475569",
  background: "#f8fafc",
  border: "1px dashed #cbd5e1",
  borderRadius: 6,
};

const errorStyle = {
  padding: "8px 12px",
  background: "#fef2f2",
  border: "1px solid #fecaca",
  borderRadius: 6,
  color: "#b91c1c",
  fontSize: 12,
  marginBottom: 12,
};

const successBoxStyle = {
  marginTop: 12,
  padding: 16,
  background: "#ecfdf5",
  border: "1px solid #a7f3d0",
  borderRadius: 8,
  textAlign: "center",
};

const footerStyle = {
  marginTop: 12,
  fontSize: 12,
  color: "#64748b",
};

const institutionTitleStyle = {
  fontWeight: 700,
  fontSize: 14,
  color: "#0f172a",
};

const institutionTextStyle = {
  fontSize: 13,
  color: "#475569",
  marginTop: 4,
  lineHeight: "1.45",
};

const resetBtnStyle = { ...secondaryBtnStyle, marginTop: 12 };

const supportLinkStyle = { color: "#0284c7", textDecoration: "underline" };
